import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MyStays from '../components/MyStays';
import DashboardRoleToggle from '../components/DashboardRoleToggle';
import MessagesPanel from '../components/MessagesPanel';
import { useApi, favorites as favoritesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getBrowsingHistory } from '../utils/browsingHistory';
import { OwnerDashboardBody } from './OwnerDashboard';

const TABS = [
  { key: 'stays', label: 'My Stays' },
  { key: 'saved', label: 'Saved Properties' },
  { key: 'recent', label: 'Recently Viewed' },
  { key: 'messages', label: 'Messages' },
];

// ── Become a Host CTA — shown to buyers who have never created a listing ────
function BecomeHostCard() {
  const navigate = useNavigate();
  return (
    <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem', display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap', borderLeft: '4px solid var(--success)' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="var(--success)" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8v1m0 8v1m0-10V4m0 18v-1" />
          <circle cx="12" cy="12" r="9" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700, marginBottom: '0.3rem' }}>Become a Host</h3>
        <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-base)', margin: 0 }}>
          Own a timeshare week you're not using? List it on ShareTime and let our price engine find you a buyer — earn money from time you'd otherwise waste.
        </p>
      </div>
      <button className="btn" style={{ background: 'var(--success)', color: 'var(--surface)', border: 'none', flexShrink: 0 }} onClick={() => navigate('/dashboard/owner?tab=new-listing')}>
        List Your Property
      </button>
    </div>
  );
}

export function BuyerDashboardBody({ hasListings = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('stays');
  const [searchParams] = useSearchParams();
  const bookingConfirmed = searchParams.get('booking') === 'confirmed';

  const { data: favData, loading: favLoading, refetch: refetchFavorites } = useApi('/favorites');
  const savedProperties = favData?.favorites || [];
  const browsingHistory = getBrowsingHistory();

  const { data: msgData } = useApi('/messages');
  const unreadMessageCount = (msgData?.conversations || []).reduce((sum, c) => sum + c.unread_count, 0);

  const [unfavoritingId, setUnfavoritingId] = useState(null);

  async function unsaveProperty(propertyId) {
    setUnfavoritingId(propertyId);
    try {
      await favoritesApi.remove(propertyId);
      refetchFavorites();
    } finally {
      setUnfavoritingId(null);
    }
  }

  return (
      <div className="dashboard">
        <h1>My Bookings</h1>
        <p className="dashboard-subtitle">Welcome back, {user?.first_name}</p>

        {!hasListings && <BecomeHostCard />}

        {bookingConfirmed && (
          <div className="alert alert-success">
            <div>Booking confirmed! Check your email for details.</div>
            <div style={{ marginTop: '0.5rem', fontSize: 'var(--text-base)', opacity: 0.85 }}>
              Note: The resort may require a credit card at check-in for incidentals. Please have a card available when you arrive.
            </div>
          </div>
        )}

        <div className="dashboard-tabs">
          {TABS.map(t => (
            <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
              {t.key === 'saved' && savedProperties.length > 0 && (
                <span style={{ marginLeft: '0.4rem', background: 'var(--ink-muted)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.1rem 0.4rem', fontWeight: 700 }}>
                  {savedProperties.length}
                </span>
              )}
              {t.key === 'messages' && unreadMessageCount > 0 && (
                <span style={{ marginLeft: '0.4rem', background: 'var(--danger)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.1rem 0.4rem', fontWeight: 700 }}>
                  {unreadMessageCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'stays' && <MyStays />}

        {tab === 'messages' && <MessagesPanel />}

        {tab === 'saved' && (
          favLoading ? <p style={{ color: 'var(--ink-muted)' }}>Loading saved properties…</p> : (
            savedProperties.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--ink-muted)' }}>
                <p>No saved properties yet.</p>
                <Link to="/search" className="btn btn-primary btn-sm" style={{ marginTop: '1rem' }}>Browse properties</Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {savedProperties.map(f => (
                  <div key={f.id} className="card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <Link to={`/properties/${f.slug || f.property_id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', textDecoration: 'none', color: 'inherit', flex: 1, minWidth: 200 }}>
                      {f.image_urls?.[0] ? (
                        <img src={f.image_urls[0]} alt={f.name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 64, height: 64, borderRadius: 'var(--radius-sm)', background: 'linear-gradient(135deg, var(--brand), var(--brand-dark))', flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ fontWeight: 600 }}>{f.name}</div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{f.location}</div>
                      </div>
                    </Link>
                    <button
                      className="btn btn-outline btn-sm"
                      disabled={unfavoritingId === f.property_id}
                      onClick={() => unsaveProperty(f.property_id)}
                    >
                      {unfavoritingId === f.property_id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )
          )
        )}

        {tab === 'recent' && (
          browsingHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--ink-muted)' }}>
              <p>No browsing history yet.</p>
              <Link to="/search" className="btn btn-primary btn-sm" style={{ marginTop: '1rem' }}>Browse properties</Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
              {browsingHistory.map(p => (
                <Link key={p.id} to={`/properties/${p.slug || p.id}`} className="card" style={{ textDecoration: 'none', color: 'inherit' }}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} style={{ width: '100%', height: 110, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: 110, background: 'linear-gradient(135deg, var(--brand), var(--brand-dark))' }} />
                  )}
                  <div style={{ padding: '0.75rem' }}>
                    <div style={{ fontSize: 'var(--text-base)', fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)' }}>{p.location}</div>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </div>
  );
}

export default function BuyerDashboard() {
  const { data: listingsData, refetch: refetchListings } = useApi('/listings/owner/mine');
  const hasListings = (listingsData?.listings || []).length > 0;
  const [view, setView] = useState('staying');

  return (
    <div className="page">
      <Navbar />
      {hasListings && <DashboardRoleToggle view={view} onChange={setView} />}
      {hasListings && view === 'hosting'
        ? <OwnerDashboardBody onCreated={refetchListings} />
        : <BuyerDashboardBody hasListings={hasListings} />}
      <Footer />
    </div>
  );
}
